/* eslint-disable no-nested-ternary */
import { Suspense, useEffect, lazy, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setContext, setTags, setUser } from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import {
  getInfo as getInfoAction,
  saveAppId,
  saveToken as saveTokenAction,
  switchWorkspace
} from '../../redux/modules/users/actions';
import { useLoginWithRedirect } from '../../providers/Auth0Provider';
import { withErrorBoundary } from '../ErrorBoundary';
import { gaInit, trackScreenView } from '../../utils/analytics';
import * as Styled from './style';
import PageLoader from '../PageLoader';
import { EXTERNAL_SESSION_TYPES, SSR_ENABLED_COMPANY_IDS } from '../../constants';
import { setupSessionReplay } from '../../utils/sentry';
import { userPilotInit } from '../../utils/userPilot.analytics';
import useConnectWiseAuth from '../../hooks/useConnectWiseAuth';
import useDispatchFactory from '../../hooks/useDispatchFactory';
import MessageForm from '../MessageForm';
import { getExternalSessionType, getSessionApp } from '../../utils';
import useAutotaskAuth from '../../hooks/useAutotaskAuth';
import { useToast } from '../../hooks';

const ConnectwiseRoutes = lazy(() => import('../../routes/connectwise'));
const AutotaskRoutes = lazy(() => import('../../routes/autotask'));
const MainRoutes = lazy(() => import('../../routes/main'));

const Layout = () => {
  const history = useHistory();
  const { user, isFetchUser, signInErrorMessage } = useSelector(store => store.users);
  const { memberId, isMemberSynced, email, company, appId, token, userId } = user;
  const saveToken = useDispatchFactory(saveTokenAction);
  const storeAppId = useDispatchFactory(saveAppId);
  const getInfo = useDispatchFactory(getInfoAction);
  const resetRedux = useDispatchFactory(switchWorkspace);
  const posthog = usePostHog();
  const { toast } = useToast();

  const routeType = useMemo(() => getExternalSessionType() || 'default', []);

  const isConnectWiseRoutes = routeType === EXTERNAL_SESSION_TYPES.CONNECTWISE;
  const isAutotaskRoutes = routeType === EXTERNAL_SESSION_TYPES.AUTOTASK;

  const handleAuthSuccess = ({ token: accessToken, appId: userAppId }) => {
    saveToken(accessToken);
    storeAppId({ appId: userAppId });
  };

  const handleNoMemberCase = () => {
    storeAppId({ appId: null });
    resetRedux();
  };

  const { loading: loadingAuthToken, errorMessage } = isConnectWiseRoutes
    ? useConnectWiseAuth({
        onSuccess: handleAuthSuccess
      })
    : isAutotaskRoutes
    ? useAutotaskAuth({
        onSuccess: handleAuthSuccess
      })
    : useLoginWithRedirect({
        shouldFetchToken: true,
        onSuccess: accessToken => handleAuthSuccess({ token: accessToken, appId })
      });

  useEffect(() => {
    trackScreenView(history.location);
    history.listen((...args) => {
      trackScreenView(...args);
      posthog?.capture('$pageview');
    });
  }, [history]);

  useEffect(() => {
    if (userId) {
      gaInit({
        options: {
          userId,
          companyName: company?.name,
          campaignId: company?.appId
        }
      });
      userPilotInit(user);
    }
  }, [userId]);

  useEffect(() => {
    if (appId && token && !userId && !isFetchUser) {
      getInfo();
    }
  }, [appId, token, userId]);

  useEffect(() => {
    if (userId && !isMemberSynced) {
      toast.error(
        'Your member is not synchronized with the PSA. Check if it exists in the PSA with the same primary email used for login.',
        {
          isDismissible: false
        }
      );
    }
  }, [userId, isMemberSynced]);

  useEffect(() => {
    // Edge case when member was removed from the workspace
    // We need to redirect this member to the workspace selection page
    if (userId && !company?.appId) {
      handleNoMemberCase();
    }
  }, [userId, company]);

  useEffect(() => {
    window.thread = {
      version: __COMMIT_HASH__ || ''
    };
    if (!memberId) {
      return;
    }

    setUser({ email, id: memberId });
    setTags({
      email,
      memberId,
      appId: company?.appId,
      companyId: company?.id,
      commitHash: __COMMIT_HASH__ || '',
      sessionType: getSessionApp()
    });
    setContext('company', {
      appId: company?.appId,
      name: company?.name,
      id: company?.id
    });
    const isSessionReplayEnabled = SSR_ENABLED_COMPANY_IDS.includes(company?.id);
    setupSessionReplay({ enabled: isSessionReplayEnabled });
  }, [memberId, email, company]);

  if (errorMessage || signInErrorMessage) {
    return (
      <Styled.Wrapper centeredContent>
        <MessageForm typeView="block">{errorMessage || signInErrorMessage}</MessageForm>
      </Styled.Wrapper>
    );
  }

  // After token is fetched, fetching user data may start with a bit delay
  // (getInfo is executed in useEffect callback)
  const isFetchingUserData = (token && appId && !memberId) || isFetchUser;
  // This condition below is supposed to ensure that user data is available before rendering the app.
  if (loadingAuthToken || isFetchingUserData) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Styled.Wrapper>
        {isConnectWiseRoutes ? (
          <ConnectwiseRoutes />
        ) : isAutotaskRoutes ? (
          <AutotaskRoutes />
        ) : (
          <MainRoutes />
        )}
      </Styled.Wrapper>
    </Suspense>
  );
};

export default withErrorBoundary(Layout);
